import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { getCardIcon, normalizeItems } from '../../AmexMarketingUtils/AmexUtils';
import './AmexSecurityCenterAiIntegrations.scss';

const listShape = PropTypes.shape({
  title: PropTypes.string,
  list_icon: PropTypes.string,
  contents: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({ content: PropTypes.string }),
  ])),
});

const isNonEmptyObject = (value) => (
  value
  && typeof value === 'object'
  && !Array.isArray(value)
  && Object.keys(value).length > 0
);

const AmexSecurityCenterAiIntegrations = ({
  awsFolderName,
  baseRequestUrl,
  aiIntegrationConfig,
}) => {
  const {
    id: sectionId = '',
    title = '',
    intro: rawIntro = [],
    list_allowed: rawAllowedItems = {},
    list_blocked: rawBlockedItems = {},
  } = aiIntegrationConfig || {};

  const intro = (Array.isArray(rawIntro) ? rawIntro : [rawIntro])
    .filter((item) => typeof item === 'string' && item.trim() !== '');
  const allowedItemsData = normalizeItems(rawAllowedItems.contents);
  const blockedItemsData = normalizeItems(rawBlockedItems.contents);
  const allowedIconPath = getCardIcon(awsFolderName, baseRequestUrl, rawAllowedItems.list_icon);
  const blockedIconPath = getCardIcon(awsFolderName, baseRequestUrl, rawBlockedItems.list_icon);
  const hasAllowedConfig = isNonEmptyObject(rawAllowedItems) && allowedItemsData.length > 0;
  const hasBlockedConfig = isNonEmptyObject(rawBlockedItems) && blockedItemsData.length > 0;
  const showCards = hasAllowedConfig || hasBlockedConfig;

  const renderIntro = () => intro.map((introItem) => (
    <p
      key={`${sectionId}-intro-${introItem}`}
      className="AmexSecurityCenterAiIntegrations__copy"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: sanitizeString(introItem) }}
    />
  ));

  const renderList = (items, iconPath, cardKey) => items.map((listItem) => (
    <li key={`${sectionId}-${cardKey}-${listItem}`} className="AmexSecurityCenterAiIntegrations__list-item">
      {iconPath && (
        <span aria-hidden="true">
          <Icon
            className="AmexSecurityCenterAiIntegrations__icon"
            url={iconPath}
          />
        </span>
      )}
      <span
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizeString(listItem) }}
      />
    </li>
  ));

  return (
    <section
      className="AmexSecurityCenterAiIntegrations"
    >
      <div className="container">
        <div className="AmexSecurityCenterAiIntegrations__wrapper">
          <h3
            id={sectionId}
            className="AmexSecurityCenterAiIntegrations__title"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitizeString(title) }}
          />
          {intro.length > 0 && renderIntro()}
        </div>
        {showCards && (
          <div className="AmexSecurityCenterAiIntegrations__cards">
            {hasAllowedConfig && (
              <div className="AmexSecurityCenterAiIntegrations__card">
                <h4
                  className="AmexSecurityCenterAiIntegrations__card-title"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizeString(rawAllowedItems?.title || '') }}
                />
                {allowedItemsData.length > 0 && (
                  <ul className="AmexSecurityCenterAiIntegrations__list">
                    {renderList(allowedItemsData, allowedIconPath, 'allowed')}
                  </ul>
                )}
              </div>
            )}
            {hasBlockedConfig && (
              <div className="AmexSecurityCenterAiIntegrations__card">
                <h4
                  className="AmexSecurityCenterAiIntegrations__card-title"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: sanitizeString(rawBlockedItems?.title || '') }}
                />
                {blockedItemsData.length > 0 && (
                  <ul className="AmexSecurityCenterAiIntegrations__list">
                    {renderList(blockedItemsData, blockedIconPath, 'blocked')}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

AmexSecurityCenterAiIntegrations.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  aiIntegrationConfig: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    intro: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    list_allowed: listShape,
    list_blocked: listShape,
  }),
};

AmexSecurityCenterAiIntegrations.defaultProps = {
  aiIntegrationConfig: {},
};

export default AmexSecurityCenterAiIntegrations;
